import { type JSX, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { shouldBlockOrgMemberAccess } from "@via/utils/helpers.tsx";
import { ROUTES } from "@via/utils/constants.tsx";

export default function InactiveOrganization(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const blockOrgMemberAccess = shouldBlockOrgMemberAccess(user);
    if (!blockOrgMemberAccess) {
      navigate(ROUTES.HOME);
      return;
    }
  }, [user]);

  return (
    <div className="flex items-center justify-center mt-4 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="text-center pb-4 pt-8 px-6">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
              <ExclamationTriangleIcon className="h-8 w-8 text-orange-600" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Organization Inactive
            </h1>
            <p className="text-gray-600">
              Access to your organization has been temporarily suspended
            </p>
          </div>

          <div className="px-6 pb-8 space-y-6">
            <div className="border border-orange-200 bg-orange-50 rounded-md p-4">
              <div className="flex items-center">
                <p className="text-orange-800 text-sm">
                  Your organization is currently inactive. Please contact your
                  organization administrator for assistance.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p className="mb-2 font-medium">What this means:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-500">
                  <li>Your account access is temporarily suspended</li>
                  <li>Data remains secure and intact</li>
                  <li>Services will resume once reactivated</li>
                </ul>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500 pt-4 border-t border-gray-200">
              Need immediate assistance? Contact us at{" "}
              <a
                href="mailto:biocore@umassmed.edu"
                className="text-blue-600 hover:underline">
                biocore@umassmed.edu
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
